/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import * as _ from 'lodash';

import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {WIDGET_CHART_DATE_OPTIONS} from '../common/constants/widget-type';
import {workspaceWhichFields} from './workspace-commons';

import viewTemplate from './workspace-gridster-item-view.component.html';
import editTemplate from './workspace-gridster-item-edit.component.html';
import {OgTranslateService} from '../common/filters/og-translate.service';
import {SearchApiService} from '../common/controller/search-api.service';
import {deferSignalStateWrite} from '../common/util/template-state';
import {APP_SHARED} from '../common/app-shared';
import {AdnSelectComponent} from '../common/directives/adn-select/adn-select.component';
import {CodemirrorFieldComponent} from '../common/directives/codemirror-field.component';
import {FORM_CONTROLS} from '../common/directives/form-controls/form-controls';
import {WorkspaceWidgetHostComponent} from './workspace-widget-host.component';

export interface WorkspaceGridsterResource {
  deleteWidget: (widget: any) => void;
  changeType: (widget: any) => void;
  whichFields: (fieldType: string, widget: any) => boolean;
  addComparisons: (widget: any) => void;
  removeComparisons: (widget: any, index: number) => void;
  markDirty: () => void;
}

const CHART_OBJECT_TYPES = ['Order', 'LineItem', 'Creative', 'EarningsAccount', 'Site', 'AdUnit'];
const FIELD_SUFFIXES = ['', '2', '3', '4', '5', '6', '7'];
const FIELD_TYPE_GROUPS = ['advFields', 'pubFields'];

@Component({
  standalone: true,
  imports: [...APP_SHARED, WorkspaceWidgetHostComponent],
  selector: 'adn-workspace-gridster-item-view',
  template: viewTemplate,
  host: {
    class: 'gridster-item-host',
    style: 'display:flex;flex-direction:column;flex:1;min-height:0;height:100%;width:100%;overflow:hidden;'
  }
})
export class WorkspaceGridsterItemViewComponent {
  @Input() widget: any;
}

@Component({
  standalone: true,
  imports: [
    ...APP_SHARED,
    FormsModule,
    ButtonsModule,
    BsDatepickerModule,
    AdnSelectComponent,
    CodemirrorFieldComponent,
    ...FORM_CONTROLS
  ],
  selector: 'adn-workspace-gridster-item-edit',
  template: editTemplate,
  host: {
    class: 'gridster-item-host',
    style: 'display:flex;flex-direction:column;flex:1;min-height:0;height:100%;width:100%;overflow:hidden;'
  }
})
export class WorkspaceGridsterItemEditComponent implements OnInit {
  @Input() widget: any;
  @Input() resource: WorkspaceGridsterResource;

  readonly chartDateOptions = WIDGET_CHART_DATE_OPTIONS;
  readonly chartObjectTypes = CHART_OBJECT_TYPES;
  readonly fieldSuffixes = FIELD_SUFFIXES;
  readonly fieldTypeGroups = FIELD_TYPE_GROUPS;
  readonly bsConfig = {dateInputFormat: 'DD/MM/YYYY'};

  readonly codemirrorDisplayDelay = signal(false);
  private readonly searchResults = signal<Record<string, any[]>>({});
  private cachedStatFields: any;
  private cachedStatFieldEntries: Array<{id: string; label: string; labelInstant?: string}> = [];
  private readonly advFieldEntriesCache = new Map<string, {fields: any; entries: Array<{key: string; label: string}>}>();
  private cachedComparisonFields: any;
  private cachedComparisonFieldEntries: Array<{id: string; name: string}> = [];
  private static readonly EMPTY_ENTRIES: any[] = [];
  private static readonly EMPTY_SEL_CONFIG: Record<string, any> = {};

  constructor(
    private readonly translate: TranslateService,
    private readonly ogTranslate: OgTranslateService,
    private readonly searchApi: SearchApiService
  ) {
  }

  ngOnInit(): void {
    deferSignalStateWrite(() => this.codemirrorDisplayDelay.set(true));
  }

  get isRealWidget(): boolean {
    return !!this.widget?.systemDefined?.widget;
  }

  get showPlaceholder(): boolean {
    return !this.widget?.systemDefined?.widget;
  }

  fieldTranslate(key: string, defaultVal?: string): string {
    return this.ogTranslate.transform(key, defaultVal);
  }

  objectTypeLabel(objectType: string, plural = false): string {
    const suffix = plural ? '.plural' : '';
    return this.fieldTranslate('object.' + objectType + suffix);
  }

  aggregationTypeLabel(type: string): string {
    return this.translate.instant('search.aggregation.types.' + type);
  }

  chartDateOptionLabel(option: string): string {
    return this.translate.instant('chart.dateOption.' + option);
  }

  whichFields(fieldType: string): boolean {
    return this.resource?.whichFields?.(fieldType, this.widget) ?? workspaceWhichFields(fieldType, this.widget);
  }

  searchItems(objectType: string): any[] {
    return this.searchResults()[objectType] || WorkspaceGridsterItemEditComponent.EMPTY_ENTRIES;
  }

  private readonly searchHandlers: Record<string, (searchElement: AdnSelectSearchElement) => void> = {};

  getSearchHandler(objectType: string): (searchElement: AdnSelectSearchElement) => void {
    if (!this.searchHandlers[objectType]) {
      this.searchHandlers[objectType] = (searchElement: AdnSelectSearchElement) => {
        const type = OBJECT_TYPE[objectType]?.type || objectType;
        this.searchApi.search({q: searchElement.search, types: type, pageSize: 50}).subscribe((data) => {
          deferSignalStateWrite(() => {
            this.searchResults.update((current) => ({
              ...current,
              [objectType]: data.searchResults || WorkspaceGridsterItemEditComponent.EMPTY_ENTRIES
            }));
          });
        });
      };
    }
    return this.searchHandlers[objectType];
  }

  isFieldChecked(fieldId: string): boolean {
    return _.indexOf(this.widget?.fields, fieldId) > -1;
  }

  toggleField(fieldId: string, checked: boolean): void {
    this.widget.fields = this.widget.fields || [];
    if (checked) {
      if (_.indexOf(this.widget.fields, fieldId) < 0) {
        this.widget.fields.push(fieldId);
      }
    } else {
      _.pull(this.widget.fields, fieldId);
    }
    this.markDirty();
  }

  statFieldEntries(): Array<{id: string; label: string; labelInstant?: string}> {
    const fields = this.widget?.systemDefined?.allFields || this.widget?.systemDefined?.statFields || [];
    if (fields !== this.cachedStatFields) {
      this.cachedStatFields = fields;
      this.cachedStatFieldEntries = _.map(fields, (f: any) => ({
        id: f.id,
        label: f.label,
        labelInstant: f.labelInstant
      }));
    }
    return this.cachedStatFieldEntries;
  }

  advFieldEntries(fieldTypes: string): Array<{key: string; label: string}> {
    const fields = this.widget?.systemDefined?.[fieldTypes];
    if (!fields) {
      return WorkspaceGridsterItemEditComponent.EMPTY_ENTRIES;
    }
    // Stable *ngFor collection — new arrays every CD recreate <option>s and
    // markForCheck-loop (NG0103) after widgets are added on edit.
    const cached = this.advFieldEntriesCache.get(fieldTypes);
    if (cached && cached.fields === fields) {
      return cached.entries;
    }
    const entries = _.map(fields, (field: any, key: string) => ({
      key,
      label: field.labelInstant || field.name
    }));
    this.advFieldEntriesCache.set(fieldTypes, {fields, entries});
    return entries;
  }

  comparisonFieldEntries(): Array<{id: string; name: string}> {
    const fields = this.widget?.systemDefined?.advFields;
    if (!fields) {
      return WorkspaceGridsterItemEditComponent.EMPTY_ENTRIES;
    }
    if (fields === this.cachedComparisonFields) {
      return this.cachedComparisonFieldEntries;
    }
    this.cachedComparisonFields = fields;
    this.cachedComparisonFieldEntries = _.map(fields, (field: any) => ({id: field.id, name: field.name}));
    return this.cachedComparisonFieldEntries;
  }

  selFieldsConfig(suffix: string): any {
    return this.widget?.systemDefined?.['selFieldsConfig' + suffix]
      || WorkspaceGridsterItemEditComponent.EMPTY_SEL_CONFIG;
  }

  showSelField(fieldTypes: string, suffix: string): boolean {
    return this.whichFields(fieldTypes) && !!this.selFieldsConfig(suffix);
  }

  showFieldsChecklist(): boolean {
    const config = this.widget?.systemDefined?.selFieldsConfig;
    return !config || config.max > 1;
  }

  showLabelField(): boolean {
    const config = this.widget?.systemDefined?.selFieldsConfig;
    return !config || config.max > 1;
  }

  onObjectTypeChange(): void {
    this.resource?.changeType?.(this.widget);
    this.markDirty();
  }

  onDateChange(): void {
    this.markDirty();
  }

  deleteWidget(): void {
    this.resource?.deleteWidget?.(this.widget);
  }

  addComparisons(): void {
    this.resource?.addComparisons?.(this.widget);
  }

  removeComparisons(index: number): void {
    this.resource?.removeComparisons?.(this.widget, index);
  }

  markDirty(): void {
    this.resource?.markDirty?.();
  }

  trackByFieldId(_index: number, field: {id: string}): string {
    return field.id;
  }
}
